import { Box, VStack } from '@chakra-ui/react';
import { ANALYTICS_EVENTS } from '../../../../constants/analytics';
import { safeAnalytics } from '../../../../utils/analytics';
import { CREATE_BODIES } from '../bodies/createBodies';
import { CreateChooseStep } from './CreateChooseStep';
import { CreateDestinationChip } from './CreateDestinationChip';
import { CreatePickerView } from './CreatePickerView';
import { DETAILS_VERB } from './createModalCopy';
import type { CreateModalController } from './useCreateModalController';
import type { useCreateChoices } from './useCreateChoices';

interface CreateModalViewProps {
  readonly ctrl: CreateModalController;
  readonly choices: ReturnType<typeof useCreateChoices>;
  readonly showShortcuts: boolean;
  readonly showPhone: boolean;
  readonly onDone: () => void;
}

/**
 * The body of whichever view the modal is on. A step 2 body stays MOUNTED
 * (hidden) while the picker is over it, so typed fields and picked files
 * survive changing the destination, including after the destination refused.
 */
export function CreateModalView({
  ctrl,
  choices,
  showShortcuts,
  showPhone,
  onDone,
}: CreateModalViewProps) {
  const { view, destination, request } = ctrl;
  if (!destination) return null;
  const openPicker = () => {
    if (view.kind === 'picker' || ctrl.busy) return;
    ctrl.setView({ kind: 'picker', returnTo: view });
  };
  const details = view.kind === 'picker' ? view.returnTo : view;
  const Body = details.kind === 'details' ? CREATE_BODIES[details.type] : undefined;
  const verb = details.kind === 'details' ? DETAILS_VERB[details.type] : undefined;
  const picking = view.kind === 'picker';

  return (
    <>
      {details.kind === 'choose' && !picking && (
        <CreateChooseStep
          actions={choices.actions}
          onSelect={(definition) => void choices.choose(definition)}
          onOpenPhone={
            showPhone ? () => ctrl.setView({ kind: 'details', type: 'phone' }) : undefined
          }
          showShortcuts={showShortcuts}
          isCommitting={choices.isCommitting}
        />
      )}
      {Body && (
        <VStack align="stretch" spacing={4} display={picking ? 'none' : 'flex'} flex={1} minH={0}>
          {verb && (
            <CreateDestinationChip
              destination={destination}
              fromView={ctrl.fromView}
              verb={verb}
              onChange={ctrl.busy ? undefined : openPicker}
            />
          )}
          <Box flex={1} minH={0} display="flex" flexDirection="column">
            <Body
              destination={destination}
              surface={request?.surface ?? 'create_modal'}
              onDone={onDone}
              onDestinationProblem={ctrl.reportDestinationProblem}
              onChangeDestination={openPicker}
              onBusyChange={ctrl.setBusy}
            />
          </Box>
        </VStack>
      )}
      {view.kind === 'picker' && (
        <CreatePickerView
          selected={destination}
          message={view.message}
          creating={view.creating ?? false}
          onCreatingChange={(creating) => ctrl.setView({ ...view, creating })}
          onPick={(next, how) => {
            ctrl.chooseDestination(next, how);
            safeAnalytics.track(ANALYTICS_EVENTS.CREATE_DESTINATION_CHANGED, {
              surface: request?.surface ?? null,
              destination_source: how,
              destination_type: next.type,
            });
            ctrl.setView(view.returnTo);
          }}
        />
      )}
    </>
  );
}
